// src/pages/Settings.js
import React from "react";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import ButtonComponent from "../components/ui/ButtonComponent";
import BillingPanel from "../components/billing/BillingPanel";

import {
  Box,
  Card,
  CardContent,
  Divider,
  Drawer,
  Stack,
  Typography,
  TextField,
  Skeleton,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";

const GET_SETTINGS_URL =
  process.env.REACT_APP_GET_ORG_SETTINGS_URL ||
  "https://getorgsettings-774073159618.us-central1.run.app";

const UPDATE_SETTINGS_URL =
  process.env.REACT_APP_UPDATE_ORG_SETTINGS_URL ||
  "https://updateorgsettings-774073159618.us-central1.run.app";

const safeString = (v) => (typeof v === "string" ? v : "");
const isValidHexColor = (v) => {
  if (!v) return true;
  return /^#([0-9a-fA-F]{6})$/.test(String(v).trim());
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

const callAuthedJson = async (url, payload = undefined) => {
  if (!url) throw new Error("Missing API URL env var for Settings.");
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated.");

  const token = await user.getIdToken();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: payload ? JSON.stringify(payload) : JSON.stringify({}),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || "Request failed.");
  return json;
};

const SectionHeader = ({ title, subtitle, actions }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
    <Box>
      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    </Box>
    {actions ? <Box sx={{ pt: 0.25 }}>{actions}</Box> : null}
  </Stack>
);

const SkeletonBlock = () => (
  <Stack spacing={1.5}>
    <Skeleton variant="text" width="45%" height={28} />
    <Skeleton variant="rounded" height={40} />
    <Skeleton variant="text" width="35%" height={28} />
    <Skeleton variant="rounded" height={40} />
    <Skeleton variant="text" width="40%" height={28} />
    <Skeleton variant="rounded" height={40} />
  </Stack>
);

const Settings = () => {
  const [billingOpen, setBillingOpen] = React.useState(false);
  const navigate = useNavigate();

  const openBilling = () => setBillingOpen(true);
  const closeBilling = () => setBillingOpen(false);

  // Loading states
  const [settingsLoading, setSettingsLoading] = React.useState(true);
  const [settingsError, setSettingsError] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");

  const [brandingSaving, setBrandingSaving] = React.useState(false);
  const [supportSaving, setSupportSaving] = React.useState(false);

  // Logo upload states
  const [logoUploading, setLogoUploading] = React.useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = React.useState(0);

  // Forms (match org doc schema: organizations/{orgId})
  const [branding, setBranding] = React.useState({
    organizationName: "",
    logoUrl: "",
    primaryColor: "",
    accentColor: "",
  });

  const [support, setSupport] = React.useState({
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    locality: "",
    region: "",
    postalCode: "",
  });

  React.useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 3500);
    return () => clearTimeout(t);
  }, [successMessage]);

  const loadSettings = React.useCallback(async () => {
    setSettingsError("");
    setSettingsLoading(true);

    try {
      const result = await callAuthedJson(GET_SETTINGS_URL);
      const settings = result?.settings || {};

      setBranding({
        organizationName: safeString(settings?.organizationName),
        logoUrl: safeString(settings?.branding?.logoUrl),
        primaryColor: safeString(settings?.branding?.primaryColor),
        accentColor: safeString(settings?.branding?.accentColor),
      });

      setSupport({
        email: safeString(settings?.support?.email),
        phone: safeString(settings?.support?.phone),

        // New org address fields (root-level in org doc via updateOrgSettings)
        addressLine1: safeString(settings?.addressLine1),
        addressLine2: safeString(settings?.addressLine2),
        locality: safeString(settings?.locality),
        region: safeString(settings?.region),
        postalCode: safeString(settings?.postalCode),
      });
    } catch (e) {
      setSettingsError(e?.message || "Failed to load settings.");
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onSaveBranding = async () => {
    setSettingsError("");
    setSuccessMessage("");

    if (!isValidHexColor(branding.primaryColor) || !isValidHexColor(branding.accentColor)) {
      return setSettingsError("Brand colors must be 6-digit hex values like #0F172A (or left blank).");
    }

    setBrandingSaving(true);
    try {
      await callAuthedJson(UPDATE_SETTINGS_URL, {
        data: {
          branding: {
            primaryColor: branding.primaryColor || null,
            accentColor: branding.accentColor || null,
          },
        },
      });

      setSuccessMessage("Branding saved.");
      await loadSettings();
    } catch (e) {
      setSettingsError(e?.message || "Failed to save branding.");
    } finally {
      setBrandingSaving(false);
    }
  };

  const onSaveSupport = async () => {
    setSettingsError("");
    setSuccessMessage("");

    setSupportSaving(true);
    try {
      await callAuthedJson(UPDATE_SETTINGS_URL, {
        data: {
          support: {
            email: support.email || null,
            phone: support.phone || null,
          },

          // Root-level org address fields
          addressLine1: support.addressLine1 || null,
          addressLine2: support.addressLine2 || null,
          locality: support.locality || null,
          region: support.region || null,
          postalCode: support.postalCode || null,
        },
      });

      setSuccessMessage("Support saved.");
      await loadSettings();
    } catch (e) {
      setSettingsError(e?.message || "Failed to save support.");
    } finally {
      setSupportSaving(false);
    }
  };

  const fileInputRef = React.useRef(null);

  const onPickLogo = () => {
    fileInputRef.current?.click();
  };

  const onLogoFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const isImage = file.type?.startsWith("image/");
    if (!isImage) return setSettingsError("Please select an image file (PNG or JPG).");

    const maxMb = 6;
    if (file.size > maxMb * 1024 * 1024) {
      return setSettingsError(`Logo is too large. Please upload an image under ${maxMb}MB.`);
    }

    setSettingsError("");
    setSuccessMessage("");
    setLogoUploading(true);
    setLogoUploadProgress(15);

    try {
      const dataUrl = await fileToDataUrl(file);
      setLogoUploadProgress(40);

      const result = await callAuthedJson(UPDATE_SETTINGS_URL, {
        data: {
          branding: {
            logoBase64: dataUrl,
            extractColors: true,
          },
        },
      });

      setLogoUploadProgress(85);

      const logoUrl = safeString(result?.logoUrl);
      const primaryHex = safeString(result?.extractedColors?.primaryHex);
      const accentHex = safeString(result?.extractedColors?.accentHex);

      setBranding((p) => ({
        ...p,
        logoUrl: logoUrl || p.logoUrl,
        primaryColor: primaryHex || p.primaryColor,
        accentColor: accentHex || p.accentColor,
      }));

      setLogoUploadProgress(100);
      setSuccessMessage(primaryHex || accentHex ? "Logo uploaded and colors detected." : "Logo uploaded.");
    } catch (err) {
      setSettingsError(err?.message || "Failed to upload logo.");
    } finally {
      setTimeout(() => setLogoUploadProgress(0), 400);
      setLogoUploading(false);
    }
  };

  const onRemoveLogo = async () => {
    setSettingsError("");
    setSuccessMessage("");

    setBrandingSaving(true);
    try {
      await callAuthedJson(UPDATE_SETTINGS_URL, {
        data: {
          branding: {
            logoBase64: null,
            logoUrl: null,
          },
        },
      });

      setBranding((p) => ({ ...p, logoUrl: "" }));
      setSuccessMessage("Logo removed.");
    } catch (e) {
      setSettingsError(e?.message || "Failed to remove logo.");
    } finally {
      setBrandingSaving(false);
    }
  };

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader title="Settings" />

        <Box sx={{ width: "100%", maxWidth: 1100, mt: 4 }}>
          <Stack spacing={2.25}>
            {settingsError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {settingsError}
              </Alert>
            ) : null}

            {successMessage ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {successMessage}
              </Alert>
            ) : null}

            {/* Billing */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <SectionHeader
                  title="Billing"
                  subtitle="View subscription status and manage billing in Stripe."
                  actions={
                    <Stack direction="row" spacing={1}>
                      <ButtonComponent label="Open Billing" onClick={openBilling} />
                      <ButtonComponent
                        label="Open Full Billing Page"
                        type="secondary"
                        onClick={() => navigate("/settings/billing")}
                      />
                    </Stack>
                  }
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Most billing tasks are handled through the Stripe Customer Portal. If you opened a deep link
                  from an invoice email, the full Billing page will also show contextual banners.
                </Typography>
              </CardContent>
            </Card>

            {/* Branding */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <SectionHeader
                  title="Branding"
                  subtitle="Logo and colors used across the app and customer-facing pages."
                  actions={
                    settingsLoading ? (
                      <Skeleton variant="rounded" width={140} height={36} />
                    ) : (
                      <ButtonComponent
                        label="Save Branding"
                        onClick={onSaveBranding}
                        disabled={brandingSaving || settingsLoading || logoUploading}
                        isLoading={brandingSaving}
                      />
                    )
                  }
                />

                <Divider sx={{ my: 2 }} />

                {settingsLoading ? (
                  <SkeletonBlock />
                ) : (
                  <Stack spacing={2.25}>
                    {/* Logo row */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          variant="rounded"
                          src={branding.logoUrl || undefined}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            bgcolor: "grey.100",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                        >
                          {branding.organizationName ? branding.organizationName[0] : "K"}
                        </Avatar>

                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            Organization Logo
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Upload a PNG/JPG. We’ll auto-detect brand colors when possible.
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Upload logo">
                          <span>
                            <IconButton
                              onClick={onPickLogo}
                              disabled={logoUploading || brandingSaving}
                              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                            >
                              <UploadFileIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Remove logo">
                          <span>
                            <IconButton
                              onClick={onRemoveLogo}
                              disabled={!branding.logoUrl || logoUploading || brandingSaving}
                              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        style={{ display: "none" }}
                        onChange={onLogoFileSelected}
                      />
                    </Stack>

                    {logoUploading && logoUploadProgress ? (
                      <Box>
                        <LinearProgress variant="determinate" value={logoUploadProgress} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                          Uploading logo…
                        </Typography>
                      </Box>
                    ) : null}

                    {/* Colors */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Primary Color"
                        value={branding.primaryColor}
                        onChange={(e) => setBranding((p) => ({ ...p, primaryColor: e.target.value }))}
                        placeholder="#0F172A"
                        size="small"
                        fullWidth
                        error={!!branding.primaryColor && !isValidHexColor(branding.primaryColor)}
                        helperText="Optional. Hex color like #0F172A"
                      />

                      <TextField
                        label="Accent Color"
                        value={branding.accentColor}
                        onChange={(e) => setBranding((p) => ({ ...p, accentColor: e.target.value }))}
                        placeholder="#3F51B5"
                        size="small"
                        fullWidth
                        error={!!branding.accentColor && !isValidHexColor(branding.accentColor)}
                        helperText="Optional. Hex color like #3F51B5"
                      />
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Tip: uploading a high-contrast logo usually produces better color detection.
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Support / Org Contact */}
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <SectionHeader
                  title="Support"
                  subtitle="Customer-facing support contact info. This information can also be embedded into PDF footers."
                  actions={
                    settingsLoading ? (
                      <Skeleton variant="rounded" width={140} height={36} />
                    ) : (
                      <ButtonComponent
                        label="Save Support"
                        onClick={onSaveSupport}
                        disabled={supportSaving || settingsLoading}
                        isLoading={supportSaving}
                      />
                    )
                  }
                />

                <Divider sx={{ my: 2 }} />

                {settingsLoading ? (
                  <SkeletonBlock />
                ) : (
                  <Stack spacing={2.25}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Support Email"
                        value={support.email}
                        onChange={(e) => setSupport((p) => ({ ...p, email: e.target.value }))}
                        placeholder="support@kinovacrm.com"
                        size="small"
                        fullWidth
                        helperText="This email may be shown in billing and system messaging."
                      />

                      <TextField
                        label="Support Phone"
                        value={support.phone}
                        onChange={(e) => setSupport((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="1 (636) 555-1234"
                        size="small"
                        fullWidth
                        helperText="Optional. If provided, it may be embedded into PDF footers."
                      />
                    </Stack>

                    <TextField
                      label="Address Line 1"
                      value={support.addressLine1}
                      onChange={(e) => setSupport((p) => ({ ...p, addressLine1: e.target.value }))}
                      placeholder="123 Main St"
                      size="small"
                      fullWidth
                    />

                    <TextField
                      label="Address Line 2"
                      value={support.addressLine2}
                      onChange={(e) => setSupport((p) => ({ ...p, addressLine2: e.target.value }))}
                      placeholder="Suite 200"
                      size="small"
                      fullWidth
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="City"
                        value={support.locality}
                        onChange={(e) => setSupport((p) => ({ ...p, locality: e.target.value }))}
                        placeholder="Chesterfield"
                        size="small"
                        fullWidth
                      />

                      <TextField
                        label="State"
                        value={support.region}
                        onChange={(e) => setSupport((p) => ({ ...p, region: e.target.value }))}
                        placeholder="Missouri"
                        size="small"
                        fullWidth
                      />

                      <TextField
                        label="Zip"
                        value={support.postalCode}
                        onChange={(e) => setSupport((p) => ({ ...p, postalCode: e.target.value }))}
                        placeholder="63005"
                        size="small"
                        fullWidth
                      />
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      PDF footer embedding uses Organization Name + (Phone • Email • Address) when available.
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ButtonComponent
                label="Refresh Settings"
                type="secondary"
                onClick={loadSettings}
                disabled={settingsLoading || brandingSaving || supportSaving || logoUploading}
              />
            </Box>
          </Stack>
        </Box>

        {/* Billing drawer */}
        <Drawer
          anchor="right"
          open={billingOpen}
          onClose={closeBilling}
          PaperProps={{
            sx: { width: { xs: "100%", sm: 520 }, p: 2 },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">Billing</Typography>
            <ButtonComponent label="Close" type="secondary" onClick={closeBilling} />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <BillingPanel variant="plain" hideTitle howBanners={false} />

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Typography variant="caption" color="text.secondary">
            For invoice deep links and return banners, use the full Billing page.
          </Typography>

          <Box sx={{ mt: 1 }}>
            <ButtonComponent
              label="Open Full Billing Page"
              type="secondary"
              onClick={() => {
                closeBilling();
                navigate("/settings/billing");
              }}
            />
          </Box>
        </Drawer>
      </div>
    </div>
  );
};

export default Settings;